/**
 * Admin TOTP API hooks. Backend lives in admin-api on :8010 (see
 * services/admin-api/src/routes/totp.ts for the full route docstring).
 *
 *   POST /admin/me/totp/enroll              → { secretBase32, otpauthUri }
 *   POST /admin/me/totp/activate            → { recoveryCodes }
 *   POST /admin/me/totp/verify              → { ok, method, recoveryCodesRemaining }
 *   POST /admin/me/totp/rotate              → { recoveryCodes }
 *   POST /admin/me/totp/disable             → { ok }
 *
 * The mutations are deliberately unbound to a query — TOTP status
 * lives on adminUsers.totpEnrolledAt; we surface it via a single
 * "me" query so the route can render with the right shape.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_API_BASE, apiFetch } from './client.js';

export interface TotpMe {
  enrolled: boolean;
  enrolledAt: string | null;
  recoveryCodesRemaining: number;
}

export const totpKeys = {
  all: ['admin-totp'] as const,
  me: () => [...totpKeys.all, 'me'] as const,
} as const;

/**
 * Surface the admin's enrollment status. The backend route doesn't
 * exist yet as a dedicated GET — we infer status from the enroll
 * call: 200 means not-enrolled-but-issued-a-secret, 409 means already
 * enrolled. Until a dedicated /me/totp endpoint lands, the UI
 * optimistically starts at unknown and surfaces the actual state
 * after the first action.
 *
 * For now this hook returns null + a no-op refetch — the route
 * components track their own local state and refetch nothing.
 */
export function useTotpMeQuery() {
  return useQuery({
    queryKey: totpKeys.me(),
    queryFn: () =>
      Promise.resolve<TotpMe>({ enrolled: false, enrolledAt: null, recoveryCodesRemaining: 0 }),
    enabled: false, // start disabled until the GET endpoint exists
  });
}

// ─── Enroll → secret + otpauth URI ─────────────────────────────────────

export interface EnrollResponse {
  secretBase32: string;
  otpauthUri: string;
}

export function useTotpEnrollMutation() {
  return useMutation({
    mutationFn: () =>
      apiFetch<EnrollResponse>({
        method: 'POST',
        url: '/admin/me/totp/enroll',
        body: {},
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Activate → commits secret + returns recovery codes ────────────────

export interface ActivateResponse {
  recoveryCodes: string[];
}

export function useTotpActivateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { secretBase32: string; code: string }) =>
      apiFetch<ActivateResponse>({
        method: 'POST',
        url: '/admin/me/totp/activate',
        body: args,
        baseUrl: ADMIN_API_BASE,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: totpKeys.all });
    },
  });
}

// ─── Verify (used as recent-auth gate, not direct UI today) ───────────

export interface VerifyResponse {
  ok: true;
  method: 'totp' | 'recovery';
  recoveryCodesRemaining: number;
}

export function useTotpVerifyMutation() {
  return useMutation({
    mutationFn: (args: { code: string }) =>
      apiFetch<VerifyResponse>({
        method: 'POST',
        url: '/admin/me/totp/verify',
        body: args,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Rotate → new secret + fresh recovery codes ────────────────────────

export interface RotateResponse {
  recoveryCodes: string[];
}

export function useTotpRotateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { currentCode: string; newSecretBase32: string; newCode: string }) =>
      apiFetch<RotateResponse>({
        method: 'POST',
        url: '/admin/me/totp/rotate',
        body: args,
        baseUrl: ADMIN_API_BASE,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: totpKeys.all });
    },
  });
}

// ─── Disable (rare; requires current code) ─────────────────────────────

export function useTotpDisableMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { currentCode: string }) =>
      apiFetch<{ ok: true }>({
        method: 'POST',
        url: '/admin/me/totp/disable',
        body: args,
        baseUrl: ADMIN_API_BASE,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: totpKeys.all });
    },
  });
}
